"use client";

/**
 * The "Something went wrong" screen used by app/error.tsx (website) and
 * app/dashboard/error.tsx (dashboard). It reports the error to the API log,
 * recovers by itself from temporary errors (lib/errorReport.ts) and lets the
 * person copy the details for support.
 */
import { startTransition, useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Button } from "@/components/Button";
import { classify, errorDetails, handleBoundaryError } from "@/lib/errorReport";

export function ErrorScreen({
  error,
  reset,
  boundary,
  backHref,
  backLabel,
  backText,
}: {
  error: Error & { digest?: string };
  reset: () => void;
  boundary: string;
  backHref: string;
  backLabel: string;
  backText: string;
}) {
  const router = useRouter();
  const [recovering, setRecovering] = useState(false);
  // Out-of-date app files (after a release or rebuild): reloading is the fix.
  const outdated = classify(error) === "chunk";
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    console.error(`${boundary} error boundary caught:`, error);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- decided once per error
    setRecovering(handleBoundaryError(error, boundary, reset));
  }, [error, boundary, reset]);

  function retry() {
    // Fetch the page's server part again too, then render it from scratch.
    startTransition(() => {
      router.refresh();
      reset();
    });
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(errorDetails(error));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setOpen(true);
    }
  }

  if (recovering) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#F6F3FF] via-bg to-[#F2EEFF] px-6" role="status" aria-live="polite">
        <div className="flex items-center gap-3 text-[0.95rem] font-semibold text-ink-soft">
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-violet/30 border-t-violet" aria-hidden />
          Reloading the page…
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#F6F3FF] via-bg to-[#F2EEFF] px-6 py-10">
      <div className="glass-card w-full max-w-[440px] rounded-[36px] p-9 text-center">
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-warn-pale text-warn">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <h1 className="mb-2 text-[1.3rem] font-extrabold">{outdated ? "This page needs a refresh" : "Something went wrong"}</h1>
        <p className="mb-7 text-[0.9rem] text-ink-soft">
          {outdated
            ? "Rankbist was updated while this page was open, so some of its files are out of date. Reloading gets the latest version."
            : <>This page hit an unexpected error. It&apos;s been logged — try again, or {backText}.</>}
        </p>
        <div className="flex flex-col gap-2.5">
          <Button variant="violet" onClick={outdated ? () => window.location.reload() : retry} className="w-full">
            {outdated ? "Reload page" : "Try again"}
          </Button>
          <Link href={backHref} className="py-2 text-[0.85rem] font-semibold text-ink-soft hover:text-ink">
            {backLabel}
          </Link>
        </div>
        <div className="mt-4 border-t border-black/5 pt-3 text-left">
          <div className="flex items-center justify-between gap-3">
            <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="text-[0.78rem] font-semibold text-ink-softer hover:text-ink">
              {open ? "Hide details" : "Show details"}
            </button>
            <button type="button" onClick={copy} className="text-[0.78rem] font-semibold text-violet-deep hover:underline">
              {copied ? "Copied" : "Copy details"}
            </button>
          </div>
          {open && (
            <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-xl bg-black/[0.04] p-3 text-[0.72rem] leading-relaxed text-ink-soft" data-no-translate>
              {`${error?.name ?? "Error"}: ${error?.message ?? ""}${error?.digest ? `\nDigest: ${error.digest}` : ""}`}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}
